import React, { useState, useEffect, startTransition } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { onIdTokenChanged, signInAnonymously } from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from './firebase-config';
import Login, { isProfileComplete } from './components/Login';
import Dashboard from './components/Dashboard';
import CreateExam from './components/CreateExam';
import PracticeExam from './components/PracticeExam';
import Instructions from './components/Instructions';
import TestWindow from './components/TestWindow';
import ResultsPage from './components/ResultsPage';
import Home from './components/Home';
import ProfilePage from './components/ProfilePage';
import AppLayout from './components/AppLayout';
import { claimedCopy } from './utils/claimGuestResult';
import { guestExamsLeft, recordGuestExam, exhaustGuestExams } from './utils/guestExams';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1d4ed8',
    },
    secondary: {
      main: '#f97316',
    },
    background: {
      default: '#f8fafc',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
  },
});

// Set by the earlier one-free-exam flow, where a guest signed in to see their result; still honoured
// for anyone mid-way through it when this version shipped.
const RESULTS_PENDING = 'guestResultsPending';
// Read-only on purpose: after a sign-in both the auth listener and the login screen route the
// user, in no fixed order, so neither may clear it. It is cleared once the result is on screen.
const hasPendingResults = () => {
  try { return localStorage.getItem(RESULTS_PENDING) === '1'; } catch { return false; }
};
const clearPendingResults = () => {
  try { localStorage.removeItem(RESULTS_PENDING); } catch { /* nothing to clear */ }
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [examSettings, setExamSettings] = useState(() => {
    const saved = localStorage.getItem('examSettings');
    return saved ? JSON.parse(saved) : null;
  });
  const [resultsData, setResultsData] = useState(() => {
    const saved = localStorage.getItem('resultsData');
    return saved ? JSON.parse(saved) : null;
  });
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [needsProfile, setNeedsProfile] = useState(false);

  useEffect(() => {
    try {
      if (examSettings) {
        const serialized = JSON.stringify(examSettings);
        // localStorage quota is ~5MB; skip oversized exams rather than throw
        if (serialized.length < 2 * 1024 * 1024) {
          localStorage.setItem('examSettings', serialized);
        } else {
          console.warn("examSettings too large for localStorage, skipping persistence.");
        }
      } else {
        localStorage.removeItem('examSettings');
      }
    } catch (e) {
      console.error("Storage Error (examSettings):", e);
    }
  }, [examSettings]);

  useEffect(() => {
    try {
      if (resultsData) {
        const serialized = JSON.stringify(resultsData);
        if (serialized.length < 2 * 1024 * 1024) {
          localStorage.setItem('resultsData', serialized);
        } else {
          localStorage.removeItem('resultsData');
        }
      } else {
        localStorage.removeItem('resultsData');
      }
    } catch (e) {
      console.error("Storage Error (resultsData):", e);
    }
  }, [resultsData]);

  // Signed-in users must finish the sign-up form before using the app
  const checkProfile = async (u) => {
    if (!u || u.isAnonymous) { setNeedsProfile(false); return false; }
    try {
      const snap = await getDoc(doc(db, 'users', u.uid));
      const incomplete = !isProfileComplete(snap.data());
      setNeedsProfile(incomplete);
      return incomplete;
    } catch (err) {
      console.error('Profile check failed:', err); // fail open: a Firestore hiccup shouldn't lock users out
      setNeedsProfile(false);
      return false;
    }
  };

  useEffect(() => {
    // onIdTokenChanged (not onAuthStateChanged) so linking a guest to Google/email also re-runs this
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        // No session: start a silent guest session so the database (auth-only) stays readable.
        signInAnonymously(auth).catch((err) => {
          console.error('Guest sign-in failed (is Anonymous auth enabled in Firebase?):', err);
          setUser(null);
          setAuthLoading(false);
          navigate('/login', { replace: true, state: { reason: 'guest-unavailable', code: err?.code } });
        });
        return;
      }

      const incomplete = await checkProfile(firebaseUser);
      setUser(firebaseUser);
      setAuthLoading(false);
      const path = window.location.pathname;
      if (path === '/' || (path === '/login' && !firebaseUser.isAnonymous && !incomplete)) {
        const resultsWaiting = !firebaseUser.isAnonymous && !incomplete && hasPendingResults();
        navigate(resultsWaiting ? '/result' : window.history.state?.usr?.next || '/home', { replace: true });
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.clear();
      exhaustGuestExams(); // a signed-out account holder doesn't get another round of free guest exams
      setExamSettings(null);
      setResultsData(null);
      navigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const isGuest = Boolean(user?.isAnonymous);

  // A signed-in user is looking at the result they earned as a guest: make sure it is saved under
  // their account so it appears in the profile, then the pending result has been delivered.
  useEffect(() => {
    if (location.pathname !== '/result' || !user || user.isAnonymous || !hasPendingResults()) return;
    const results = resultsData;
    if (!claimedCopy(results, user.uid)) { clearPendingResults(); return; } // already this account's
    let cancelled = false;
    (async () => {
      try {
        const guestRef = doc(db, 'results', results.resultId);
        // Prefer the stored result (the trimmed copy the exam saved); fall back to the one in memory.
        const stored = await getDoc(guestRef).then((snap) => (snap.exists() ? snap.data() : null)).catch(() => null);
        const copy = claimedCopy(stored ? { ...stored, resultId: results.resultId } : results, user.uid);
        await setDoc(doc(db, 'results', copy.resultId), copy);
        // Fine if rules forbid this: nobody can sign in as that guest again.
        deleteDoc(guestRef).catch(() => {});
        clearPendingResults();
        if (!cancelled) setResultsData((prev) => (prev?.resultId === results.resultId ? { ...prev, resultId: copy.resultId, userId: user.uid } : prev));
      } catch (err) {
        // The flag stays, so the move is retried the next time they land on this result.
        console.error('Could not move the guest result to this account:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname, user, resultsData]);
  const guestLimitReached = isGuest && guestExamsLeft() === 0;
  const askToSignIn = (next) => navigate('/login', { state: { reason: 'limit', next } });

  // Every way into an exam goes through here
  const openInstructions = (settings) => {
    // A scheduled exam (opened from a shared link or the list) is reopened after signing in
    if (guestLimitReached) return askToSignIn(settings?.docId ? `/scheduled-exams/${settings.docId}` : undefined);
    setExamSettings(settings);
    navigate('/instructions');
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw', bgcolor: '#f8fafc' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh' }}>
        {needsProfile && location.pathname !== '/login' ? <Navigate to="/login" replace /> : (
        <Routes>
          <Route path="/login" element={
            <Login
              reason={location.state?.reason}
              reasonCode={location.state?.code}
              onLoginComplete={async () => {
                if (await checkProfile(auth.currentUser)) return;
                // The auth listener may not have swapped the guest for this account yet; without this,
                // /result would still see a guest and bounce straight back to /login.
                setUser(auth.currentUser);
                navigate(hasPendingResults() ? '/result' : location.state?.next || '/home', { replace: true });
              }}
              incompleteProfileUser={needsProfile ? auth.currentUser : null}
              onProfileSaved={() => checkProfile(auth.currentUser)}
              onContinueAsGuest={isGuest && !guestLimitReached ? () => navigate('/home', { replace: true }) : undefined}
            />
          } />
          
          {/* Pages inside the sidebar shell */}
          <Route element={user ? <AppLayout onLogout={handleLogout} /> : <Navigate to="/login" />}>
          <Route path="/scheduled-exams/:examId?" element={
            user ? (
              <Dashboard
                onPracticeClick={() => navigate('/practice-exam')}
                onStartExamClick={openInstructions}
              />
            ) : <Navigate to="/login" />
          } />

          <Route path="/create-exam" element={
            user ? (
              <CreateExam
                onNextClick={openInstructions}
              />
            ) : <Navigate to="/login" />
          } />

          <Route path="/home" element={user ? <Home /> : <Navigate to="/login" />} />

          <Route path="/practice-exam" element={
            user ? (
              <PracticeExam onNextClick={openInstructions} />
            ) : <Navigate to="/login" />
          } />

          {/* Guests see their own history and stats too, with a sign-in prompt in place of personal details */}
          <Route path="/profile" element={
            user ? (
              <ProfilePage
                onSelectPastExam={(exam) => {
                  setResultsData(exam); 
                  navigate('/result');
                }}
              />
            ) : <Navigate to="/login" />
          } />

          <Route path="/result" element={
            user ? (
              <ResultsPage
                resultsData={resultsData}
                onBackToDashboard={() => navigate('/home')}
              />
            ) : <Navigate to="/login" replace />
          } />
          </Route>

          {/* Focused, full-screen exam flow */}
          <Route path="/instructions" element={
            user && !guestLimitReached ? (
              <Instructions
                onBackClick={() => navigate(-1)}
                onStartTestClick={(settings) => {
                  setExamSettings(settings);
                  navigate('/exam');
                }}
                examSettings={examSettings}
              />
            ) : <Navigate to="/login" state={guestLimitReached ? { reason: 'limit' } : undefined} />
          } />

          <Route path="/exam" element={
            // No exam in progress (finished, left, or reached via Back): nothing to show here
            user && !guestLimitReached ? (examSettings ? (
              <TestWindow
                examSettings={examSettings}
                onLeave={() => {
                  navigate('/practice-exam', { replace: true });
                  setExamSettings(null);
                }}
 onFinish={(results) => {
                  if (isGuest) recordGuestExam();
                  navigate('/result', { replace: true });
                  // Same low-priority batch as the navigation: any update on its own re-renders /exam first,
                  // where the "no exam" or "guest limit reached" redirect would win over /result.
                  startTransition(() => {
                    setResultsData(results);
                    setExamSettings(null);
                  });
                }}
              />
            ) : <Navigate to="/practice-exam" replace />) : <Navigate to="/login" state={guestLimitReached ? { reason: 'limit' } : undefined} />
          } />

          <Route path="/dashboard" element={<Navigate to="/scheduled-exams" replace />} />

          <Route path="*" element={<Navigate to={user ? "/home" : "/login"} replace />} />
        </Routes>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default App;
